import { useGetFormDetail2 } from "@/queries/formaly-request";
import type { ApiFlowsExecutionsResponseType } from "@/types/entities/flows";
import type { FormDetailResponse } from "@/types/entities/formality-request";
import { formatDateTime } from "@/utils/dates";
import { cn } from "@/utils/theme";
import {
  Badge,
  Button,
  Icon,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@elaniintc/goes-design-system";
import { useMemo, useState } from "react";
import { getExecutionBadge } from ".";

interface Props {
  formalityId: string;
  executions: ApiFlowsExecutionsResponseType["data"][number]["node_executions"];
}

const RevisionSimpleContent = ({ executions, formalityId }: Props) => {
  const localSorted = useMemo(() => {
    return executions.sort((a, b) => {
      const dateA = a.ended_at ? new Date(a.ended_at) : new Date(a.started_at);
      const dateB = b.ended_at ? new Date(b.ended_at) : new Date(b.started_at);
      return dateB.getTime() - dateA.getTime();
    });
  }, [executions]);

  return (
    <div className="w-full flex flex-col gap-4">
      {localSorted
        .filter((_, index) => {
          return index % 2 === 0;
        })
        .map((e, index, { length }) => {
          return (
            <Execution
              key={e.id}
              order={length - index}
              formalityId={formalityId}
              execution={e}
            />
          );
        })}
    </div>
  );
};

const Execution = ({
  order,
  execution,
  formalityId,
}: {
  order: number;
  formalityId: string;
  execution: ApiFlowsExecutionsResponseType["data"][number]["node_executions"][number];
}) => {
  const [isOpen, setIsOpen] = useState(true);
  const form = execution.inputs.find(
    (i) => i.name === "form" && i.type === "FORM",
  );
  const formId = form?.value as string;
  const { data, isFetching } = useGetFormDetail2(formId, formalityId);

  return (
    <div
      key={execution.id}
      className="border border-neutral rounded-lg p-4 flex flex-col gap-4"
    >
      <div className="flex justify-between items-start">
        <div>
          <p>Estado del nodo: {getExecutionBadge(execution.status)}</p>
          <p>Inició: {formatDateTime(execution.started_at)}</p>
          {execution.ended_at && (
            <p>Terminó: {formatDateTime(execution.ended_at || "")}</p>
          )}
          {execution.status === "COMPLETED" && (
            <p>
              Resultado:
              {execution.outputs.length > 0
                ? (execution.outputs?.[0].value as boolean)
                  ? "Aprobado"
                  : " Rechazado"
                : ""}
            </p>
          )}
        </div>
        <Button
          aria-label={isOpen ? "Cerrar" : "Abrir"}
          size="icon"
          variant="tertiary"
          onClick={() => setIsOpen(!isOpen)}
          data-testid={`toggle-button-${execution.id}`}
        >
          <Icon type="far" name={isOpen ? "chevron-up" : "chevron-down"} />
        </Button>
      </div>
      <div className={isOpen ? "" : "hidden"}>
        {isFetching ? (
          <div className="w-full h-20">
            <Icon name="spinner-third" type="fas" className="animate-spin" />
          </div>
        ) : (
          <>
            {data?.steps.map((step) => {
              return <Step order={order} step={step} key={step.id} />;
            })}
          </>
        )}
      </div>
    </div>
  );
};

const Step = ({
  step,
  order,
}: {
  order: number;
  step: FormDetailResponse["steps"][number];
}) => {
  const [isOpen, setIsOpen] = useState(true);
  return (
    <div
      key={step.id}
      className="border border-neutral rounded-lg p-4 flex flex-col gap-2"
    >
      <div className="flex justify-between items-center">
        <p>{step.name}</p>
        <Button
          aria-label={isOpen ? "Cerrar" : "Abrir"}
          size="icon"
          variant="tertiary"
          onClick={() => setIsOpen(!isOpen)}
          data-testid={`toggle-button-${step.id}`}
        >
          <Icon type="far" name={isOpen ? "chevron-up" : "chevron-down"} />
        </Button>
      </div>
      <div
        className={cn(!isOpen && "hidden")}
        data-testid={`step-content-${step.id}`}
      >
        {step.sections.map((section) => {
          return <Section order={order} section={section} key={section.id} />;
        })}
      </div>
    </div>
  );
};

const Section = ({
  order,
  section,
}: {
  order: number;
  section: FormDetailResponse["steps"][number]["sections"][number];
}) => {
  const [isOpen, setIsOpen] = useState(true);
  return (
    <div className="border border-neutral rounded-lg p-4 bg-neutral-object">
      <div className="flex justify-between items-center ">
        <h3>{section.title}</h3>
        <Button
          aria-label={isOpen ? "Cerrar" : "Abrir"}
          size="icon"
          variant="tertiary"
          onClick={() => setIsOpen(!isOpen)}
        >
          <Icon type="far" name={isOpen ? "chevron-up" : "chevron-down"} />
        </Button>
      </div>
      <div className={cn("px-4 flex flex-col gap-4", !isOpen && "hidden")}>
        {section.fields.map((field) => {
          const filledFieldSorted = field.filled_fields
            ?.sort((a, b) => {
              return (
                new Date(b.created_at).getTime() -
                new Date(a.created_at).getTime()
              );
            })
            .slice(-order);

          return (
            <div key={field.id} className="flex gap-4 items-start">
              <p className="w-1/4">{field.label}: </p>
              <FilledField field={filledFieldSorted} />
            </div>
          );
        })}
      </div>
    </div>
  );
};

const FilledField = ({
  field,
}: {
  field:
    | FormDetailResponse["steps"][number]["sections"][number]["fields"][number]["filled_fields"]
    | undefined;
}) => {
  const [isOpen, setIsOpen] = useState(true);
  const firstField = field?.[0];
  const correction = firstField?.correction;
  return (
    <div
      className={cn(
        "flex flex-col w-full p-2",
        (field?.length || 0) > 1 && "border border-neutral-loud rounded-lg ",
      )}
    >
      <div className="w-full flex justify-between gap-4">
        <div className="flex gap-1">
          <Tooltip>
            <TooltipTrigger asChild>
              <Badge
                shape="square"
                size="sm"
                className="size-6 flex items-center justify-center rounded-lg"
                variant={
                  correction?.status === "APPROVED" ? "success" : "danger"
                }
              >
                <Icon
                  name={correction?.status === "APPROVED" ? "check" : "times"}
                  type="fas"
                  className={cn(
                    "text-xs",
                    correction?.status === "APPROVED" && "text-success-quiet",
                    correction?.status === "DENIED" && "text-danger-quiet",
                  )}
                />
              </Badge>
            </TooltipTrigger>
            <>
              {(correction?.comments?.length || 0) > 0 && (
                <TooltipContent side="top">
                  <p>{correction?.comments?.[0].text}</p>
                </TooltipContent>
              )}
            </>
          </Tooltip>
          <FilledFieldValue value={firstField?.value || ""} />
        </div>
        {(field?.length || 0) > 1 && (
          <Button
            aria-label={isOpen ? "Cerrar" : "Abrir"}
            size="icon"
            variant="tertiary"
            onClick={() => setIsOpen(!isOpen)}
          >
            <Icon type="far" name={isOpen ? "chevron-up" : "chevron-down"} />
          </Button>
        )}
      </div>
      {(field?.length || 0) > 1 && isOpen && (
        <div className="flex flex-col gap-2">
          {field?.slice(1).map((f) => {
            const correction = f.correction;
            return (
              <div key={f.id} className="flex gap-1 items-start">
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Badge
                      shape="square"
                      size="sm"
                      className="size-6 flex items-center justify-center rounded-lg"
                      variant={
                        correction?.status === "APPROVED" ? "success" : "danger"
                      }
                    >
                      <Icon
                        name={
                          correction?.status === "APPROVED" ? "check" : "times"
                        }
                        type="fas"
                        className={cn(
                          "text-xs",
                          correction?.status === "APPROVED" &&
                            "text-success-quiet",
                          correction?.status === "DENIED" &&
                            "text-danger-quiet",
                        )}
                      />
                    </Badge>
                  </TooltipTrigger>
                  <>
                    {(correction?.comments?.length || 0) > 0 && (
                      <TooltipContent side="top">
                        <p>{correction?.comments?.[0].text}</p>
                      </TooltipContent>
                    )}
                  </>
                </Tooltip>
                <FilledFieldValue value={f.value} />
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

const FilledFieldValue = ({
  value,
}: {
  value:
    | string
    | number
    | string[]
    | {
        start: string;
        end: string;
      };
}) => {
  console.log(value);
  if (Array.isArray(value)) {
    return (
      <ul>
        {value.map((v, idx) => (
          <li key={idx}>{v}</li>
        ))}
      </ul>
    );
  } else if (
    typeof value === "object" &&
    value !== null &&
    "start" in value &&
    "end" in value
  ) {
    return (
      <span>
        {value.start} - {value.end}
      </span>
    );
  } else if (
    typeof value === "object" &&
    value !== null &&
    "type" in value &&
    "value" in value
  ) {
    return (
      <span>
        {
          (
            value as {
              type: string;
              value: string;
            }
          ).value
        }{" "}
        (
        {(
          value as {
            type: string;
            value: string;
          }
        ).type.toUpperCase()}
        )
      </span>
    );
  } else {
    return <span>{value}</span>;
  }
};

export default RevisionSimpleContent;
